import { jsx, jsxs } from "react/jsx-runtime";
import react, { useEffect } from "react";
import styled_components from "styled-components";
import { Absolute, Box, Flex, Relative } from "pcln-design-system";
import { Close, MicOff, Microphone } from "pcln-icons";
import BotWithAnimatedBorder from "./components/Realtime/BotWithAnimatedBorder/BotWithAnimatedBorder.js";
import RealTimeHeader from "./components/Realtime/RealTimeHeader/RealTimeHeader.js";
import RenderMarkers from "./components/Realtime/RenderMarkers/RenderMarkers.js";
import analytics from "./analytics/analytics.js";
import { useRealTimeChatCoordinator } from "./hooks/useRealTimeChatCoordinator.js";
import { browserStorageManager } from "./utils/browserStorage.js";
import { useChatMessagesContext } from "./hooks/useChatMessagesContext.js";
import { useLoadingStateContext } from "./hooks/useLoadingStateContext.js";
import { useRealTimeContext } from "./hooks/useRealTimeContext.js";
const Container = /*#__PURE__*/ styled_components(Box).withConfig({
    displayName: "RealTimeAudioPanel__Container"
})([
    "z-index:1510;height:",
    ";left:",
    ";top:",
    ";width:100%;position:absolute;background:linear-gradient( 144.03deg,#7701bb 26.24%,#7601bb 30.85%,#7503bc 35.32%,#7205bd 39.67%,#6e09bf 43.9%,#690dc1 48.03%,#6312c4 52.1%,#5c18c7 56.1%,#541fca 60.07%,#4c27ce 64.01%,#422fd2 67.94%,#3738d7 71.89%,#2c42dc 75.87%,#204de1 79.89%,#1358e7 83.98%,#0564ed 88.15% ),radial-gradient( 37.63% 77.06% at -3.33% 9.11%,rgba(245,235,250,0.6) 0%,rgba(238,222,246,0.51875) 16.15%,rgba(232,210,243,0.441421) 33.33%,rgba(209,168,232,0.174338) 66.15%,rgba(201,153,228,0.0827664) 82.29%,rgba(194,140,224,0) 100% ),radial-gradient( 22.92% 32.88% at 93.06% 103.5%,rgba(232,242,255,0) 0%,rgba(232,242,255,0) 0.01%,rgba(153,195,249,0) 100% );"
], ({ isModal })=>isModal ? '100%' : 'calc(100% - 66px)', ({ isModal })=>isModal ? 'unset' : '0', ({ isModal })=>isModal ? 'unset' : '66px');
const HeaderWrapper = /*#__PURE__*/ styled_components(Flex).withConfig({
    displayName: "RealTimeAudioPanel__HeaderWrapper"
})([
    "height:57px;justify-content:center;align-items:center;position:absolute;top:0;z-index:1;"
]);
const BodyWrapper = /*#__PURE__*/ styled_components(Flex).withConfig({
    displayName: "RealTimeAudioPanel__BodyWrapper"
})([
    "flex-grow:1;justify-content:center;align-items:center;flex-direction:column;"
]);
const MarkerPanel = /*#__PURE__*/ styled_components(Box).withConfig({
    displayName: "RealTimeAudioPanel__MarkerPanel"
})([
    "overflow-y:auto;position:relative;"
]);
const FooterWrapper = /*#__PURE__*/ styled_components(Absolute).withConfig({
    displayName: "RealTimeAudioPanel__FooterWrapper"
})([
    "bottom:16px;height:48px;width:100%;background:transparent;"
]);
const ControlWrapper = /*#__PURE__*/ styled_components(Flex).withConfig({
    displayName: "RealTimeAudioPanel__ControlWrapper"
})([
    "align-items:center;justify-content:space-between;height:48px;"
]);
const Mic = /*#__PURE__*/ styled_components(Box).withConfig({
    displayName: "RealTimeAudioPanel__Mic"
})([
    "cursor:pointer;border-radius:50%;width:45px;height:45px;background:transparent;display:flex;align-items:center;justify-content:center;"
]);
const RealTimeAudioPanel_Close = /*#__PURE__*/ styled_components(Mic).withConfig({
    displayName: "RealTimeAudioPanel__Close"
})([
    "background:transparent;"
]);
const ChildWrapper = /*#__PURE__*/ styled_components(Box).withConfig({
    displayName: "RealTimeAudioPanel__ChildWrapper"
})([
    "height:100%;display:flex;position:absolute;bottom:10%;"
]);
const RealTimeAudioPanel = ({ realtimeContext, locale, requestFrom, isCustomVoice = false, isModal = false })=>{
    const { setLoadingStatus } = useLoadingStateContext();
    const { setStartRealTime } = useRealTimeContext();
    const { setChatMessages } = useChatMessagesContext();
    const pennysid = browserStorageManager.getBrowserStorage()?.getItem('pennysid') ?? '';
    const { isMuted, messages, marker, mediaStream, startChat, stopChat, toggleMute, setMarker } = useRealTimeChatCoordinator({
        locale,
        realtimeContext,
        analytics: analytics,
        baseUrl: window.location.origin,
        onLoadingChange: setLoadingStatus,
        onOpenStateChange: setStartRealTime,
        pennysid,
        requestFrom,
        isCustomVoice
    });
    useEffect(()=>{
        startChat();
    }, []);
    useEffect(()=>{
        setChatMessages(messages);
    }, [
        messages
    ]);
    function handleStopChatClick() {
        stopChat();
        setStartRealTime(false);
    }
    return /*#__PURE__*/ jsx(Container, {
        isModal: isModal,
        "data-testid": "real-time-audio-panel",
        children: /*#__PURE__*/ jsx(Flex, {
            flexDirection: 'column',
            children: /*#__PURE__*/ jsxs(BodyWrapper, {
                children: [
                    /*#__PURE__*/ jsx(HeaderWrapper, {
                        children: /*#__PURE__*/ jsx(RealTimeHeader, {})
                    }),
                    marker?.markerItem?.displayComponentName ? /*#__PURE__*/ jsx(MarkerPanel, {
                        "data-testid": "marker-panel",
                        children: /*#__PURE__*/ jsx(RenderMarkers, {
                            markerToDisplay: marker,
                            "data-testid": "marker-display",
                            setMarker: setMarker
                        })
                    }) : /*#__PURE__*/ jsx(ChildWrapper, {
                        children: /*#__PURE__*/ jsx(BotWithAnimatedBorder, {
                            isMuted: isMuted,
                            mediaStream: mediaStream
                        })
                    }),
                    /*#__PURE__*/ jsx(FooterWrapper, {
                        children: /*#__PURE__*/ jsxs(ControlWrapper, {
                            px: 3,
                            children: [
                                /*#__PURE__*/ jsx(Mic, {
                                    onClick: toggleMute,
                                    "data-testid": "toggle-mute",
                                    children: isMuted ? /*#__PURE__*/ jsx(Relative, {
                                        top: "3px",
                                        children: /*#__PURE__*/ jsx(MicOff, {
                                            size: '30px',
                                            color: '#fff',
                                            "data-testid": "mute-enabled"
                                        })
                                    }) : /*#__PURE__*/ jsx(Relative, {
                                        top: "3px",
                                        children: /*#__PURE__*/ jsx(Microphone, {
                                            size: '30px',
                                            color: '#fff',
                                            "data-testid": "mute-disabled"
                                        })
                                    })
                                }),
                                /*#__PURE__*/ jsx(RealTimeAudioPanel_Close, {
                                    onClick: handleStopChatClick,
                                    "data-testid": "close-chat",
                                    children: /*#__PURE__*/ jsx(Close, {
                                        size: '30px',
                                        color: '#fff'
                                    })
                                })
                            ]
                        })
                    })
                ]
            })
        })
    });
};
const src_RealTimeAudioPanel = /*#__PURE__*/ react.memo(RealTimeAudioPanel);
export { src_RealTimeAudioPanel as default };
